// ============================================================================
// MODULLAR KATALOGI
//
// Restoranda nima bor — shu ro'yxatdan yoqiladi yoki o'chiriladi.
// Yangi imkoniyat har doim shu yerga modul sifatida qo'shiladi va
// avval 'planned' holatda turadi. U tayyor bo'lganda 'available' ga o'tadi.
// ============================================================================

export type ModuleStatus = 'available' | 'planned';

export interface ModuleDef {
  key: string;
  label: string;
  description: string;
  status: ModuleStatus;
  /** Har doim yoqiq, o'chirib bo'lmaydi */
  alwaysOn?: boolean;
  /** Bu modul ishlashi uchun yoqilgan bo'lishi kerak bo'lgan boshqa modullar */
  requires?: readonly string[];
}

export const MODULES = [
  {
    key: 'sales',
    label: 'Savdo va smena',
    description: 'Kassa, to‘lov, chek, smena va kassa harakatlari',
    status: 'available',
    alwaysOn: true,
  },
  {
    key: 'inventory',
    label: 'Sklad va retsept',
    description: 'Xom ashyo, kirim, retsept bo‘yicha avtomatik sarf, inventarizatsiya, tannarx',
    status: 'available',
  },
  {
    key: 'hall',
    label: 'Zal va stollar',
    description: 'Zonalar, stollar, stolga buyurtma',
    status: 'available',
  },
  {
    key: 'kds',
    label: 'Oshxona ekrani',
    description: 'Buyurtmalar stansiyalar bo‘yicha ekranga chiqadi',
    status: 'available',
  },
  {
    key: 'expenses',
    label: 'Xarajatlar',
    description: 'Kassadan va tashqaridan xarajatlar, toifalar bo‘yicha',
    status: 'available',
  },
  {
    key: 'staff_meals',
    label: 'Xodim ovqati',
    description: 'Xodimlarga tekin ovqat, skladdan ayiriladi, savdoga kirmaydi',
    status: 'available',
    requires: ['inventory'],
  },
  {
    key: 'multi_branch',
    label: 'Ko‘p filial',
    description: 'Bir nechta filial, filiallar bo‘yicha hisobot',
    status: 'available',
  },
  {
    key: 'waiters',
    label: 'Ofitsiant ilovasi',
    description: 'Ofitsiant telefonidan stolga buyurtma, “mening stollarim”, hisob cheki kassada, to‘lov (sozlamaga qarab)',
    status: 'available',
    requires: ['hall'],
  },
  {
    key: 'advisor',
    label: 'Maslahatchi',
    description: 'Ertaga nima tayyorlash va nima xarid qilish kerak: har taom prognozi, xarid ro‘yxati, kundalik maslahatlar',
    status: 'available',
  },
  {
    key: 'aggregators',
    label: 'Agregatorlar',
    description: 'Uzum, Yandex va boshqa kanallar: kanal narxi, komissiya, sof foyda, API orqali kelgan buyurtmalar',
    status: 'available',
  },
  {
    key: 'kitchen_printers',
    label: 'Oshxona printerlari',
    description: 'Stansiya bo‘yicha tarmoq printerlariga chek (ONE POS Agent orqali)',
    status: 'planned',
  },
  {
    key: 'queue_screen',
    label: 'Mijoz navbat ekrani',
    description: '“Buyurtmangiz tayyor” raqamlari ekrani',
    status: 'planned',
  },
  {
    key: 'delivery',
    label: 'Yetkazib berish',
    description: 'Manzil, kuryer, yetkazish narxi',
    status: 'planned',
  },
  {
    key: 'loyalty',
    label: 'Mijozlar va bonus',
    description: 'Mijozlar bazasi, bonus va keshbek',
    status: 'planned',
  },
] as const satisfies readonly ModuleDef[];

export type ModuleKey = (typeof MODULES)[number]['key'];

const MODULE_MAP: ReadonlyMap<string, ModuleDef> = new Map(MODULES.map((m) => [m.key, m as ModuleDef] as const));

export function getModule(key: string): ModuleDef | undefined {
  return MODULE_MAP.get(key);
}

export function isModuleKey(key: string): key is ModuleKey {
  return MODULE_MAP.has(key);
}

/**
 * Yoqilgan modullar to'plamini tekshiradi: hammasi mavjudmi, rejadagi modul yoqilmaganmi,
 * bog'liqliklar bajarilganmi. Bo'sh ro'yxat = hammasi joyida.
 */
export function validateModules(enabled: readonly string[]): string[] {
  const errors: string[] = [];
  const set = new Set(enabled);
  for (const key of enabled) {
    const m = MODULE_MAP.get(key);
    if (!m) {
      errors.push(`Noma’lum modul: ${key}`);
      continue;
    }
    if (m.status !== 'available') errors.push(`"${m.label}" moduli hali tayyor emas`);
    for (const dep of m.requires ?? []) {
      if (!set.has(dep)) {
        errors.push(`"${m.label}" uchun "${MODULE_MAP.get(dep)?.label ?? dep}" yoqilgan bo‘lishi kerak`);
      }
    }
  }
  for (const m of MODULES) {
    if ('alwaysOn' in m && m.alwaysOn && !set.has(m.key)) {
      errors.push(`"${m.label}" har doim yoqiq bo‘lishi kerak`);
    }
  }
  return errors;
}
